
import { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

export default function Contact() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const questions = [
    "What if one conversation could change everything?",
    "Why wait when the future is calling?",
    "How do revolutionary partnerships begin?",
    "What if your next message sparked a transformation?"
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % questions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3prvm3tpgsu3r1tnm2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: "ri-phone-line",
      title: "Revolutionary Hotline",
      description: "Speak directly with our transformation specialists",
      contact: "+234 916 771 0173",
      action: "Call Now",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: "ri-mail-line",
      title: "Digital Command Center",
      description: "Send us your revolutionary ideas and questions",
      contact: "info@thespire.com.ng",
      action: "Email Us",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: "ri-whatsapp-line",
      title: "Instant Revolution",
      description: "Connect with us instantly for immediate support",
      contact: "WhatsApp Chat",
      action: "Chat Now",
      color: "from-green-500 to-teal-600"
    },
    {
      icon: "ri-map-pin-line",
      title: "Innovation Headquarters",
      description: "Visit our revolutionary workspace and innovation hub",
      contact: "Abuja, Nigeria",
      action: "Get Directions",
      color: "from-purple-500 to-pink-600"
    }
  ];

  const services = [
    "Educational Technology Solutions",
    "Device-as-a-Service (DaaS)",
    "Custom Software Development",
    "AI & Machine Learning Solutions",
    "Emergency Response Systems",
    "Training & Workshops",
    "Consulting Services",
    "Other"
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact TheSpire - Revolutionary Tech Solutions",
    "description": "Connect with TheSpire for revolutionary tech training, software development, and educational technology solutions. Transform your future with us.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Wuse 2",
        "addressLocality": "Abuja",
        "addressRegion": "Federal Capital Territory",
        "addressCountry": "Nigeria"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+234-916-771-0173",
          "contactType": "customer service",
          "email": "info@thespire.com.ng",
          "availableLanguage": "English"
        },
        {
          "@type": "ContactPoint",
          "url": "https://wa.me/2349167710173",
          "contactType": "customer service"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "TheSpire Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Revolutionary Tech Training",
              "description": "Transform your skills with cutting-edge technology courses"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Software Development",
              "description": "Custom software solutions that dominate markets"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Device-as-a-Service",
              "description": "Educational technology infrastructure for schools"
            }
          }
        ]
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <SEO
        title="Contact TheSpire - Revolutionary Tech Solutions"
        description="Connect with TheSpire for revolutionary tech training, software development, and educational technology solutions. Transform your future with us."
        keywords="contact thespire, tech training contact, software development nigeria, educational technology, abuja tech company"
        structuredData={structuredData}
      />
      
      {/* Dynamic Background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 25%, transparent 50%)`
        }}
      />
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#6D03F4] rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-[#6D03F4]/20 via-transparent to-[#007FFF]/20" />
        
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          {/* Rotating Questions */}
          <div className="mb-8 h-16 flex items-center justify-center">
            <p className="text-lg md:text-xl text-[#6D03F4] font-medium transition-all duration-1000 transform">
              {questions[currentQuestion]}
            </p>
          </div>

          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-none">
            <span className="block text-white">LET'S BUILD</span>
            <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
              THE FUTURE
            </span>
          </h1>
          
          <p className="text-xl md:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto font-light">
            Ready to <span className="text-[#6D03F4] font-bold">revolutionize</span> your world? 
            Let&apos;s start a conversation that <span className="text-[#007FFF] font-bold">changes everything</span>.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button size="lg" className="bg-gradient-to-r from-[#6D03F4] to-[#007FFF] hover:from-[#5A02D1] hover:to-[#0066CC] text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
              START REVOLUTION
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-2 border-[#6D03F4] text-[#6D03F4] hover:bg-[#6D03F4] hover:text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              EXPLORE SOLUTIONS
            </Button>
          </div>
        </div>

        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(109, 3, 244, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(109, 3, 244, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            animation: 'grid-move 20s linear infinite'
          }} />
        </div>
      </section>

      {/* Revolutionary Contact Methods */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              CONNECT WITH
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                REVOLUTIONARIES
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Multiple ways to reach our <span className="text-[#6D03F4] font-bold">transformation specialists</span> and start your journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => (
              <div key={index} className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:border-[#6D03F4]/50 transition-all duration-500 hover:transform hover:scale-105">
                <div className={`w-16 h-16 bg-gradient-to-r ${method.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${method.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{method.title}</h3>
                <p className="text-gray-300 mb-4">{method.description}</p>
                <p className="text-[#6D03F4] font-semibold mb-4">{method.contact}</p>
                <button className="w-full bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white py-2 px-4 rounded-lg hover:from-[#5A02D1] hover:to-[#0066CC] transition-all duration-300 font-bold transform hover:scale-105 whitespace-nowrap">
                  {method.action}
                </button>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4]/20 to-[#007FFF]/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary Contact Form */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              START YOUR
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                TRANSFORMATION
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300">
              Tell us about your <span className="text-[#6D03F4] font-bold">revolutionary vision</span> and let's make it reality
            </p>
          </div>

          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-gradient-to-r from-green-500/20 to-emerald-600/20 border border-green-500/30 text-green-400 rounded-lg text-center">
                <h3 className="font-bold text-lg mb-2">Revolution Initiated! 🚀</h3>
                <p>Your message has been received. Our transformation specialists will contact you within 24 hours to discuss your revolutionary project.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-gradient-to-r from-red-500/20 to-orange-600/20 border border-red-500/30 text-red-400 rounded-lg text-center">
                <h3 className="font-bold text-lg mb-2">Transmission Failed</h3>
                <p>Something went wrong. Please try again or contact us directly.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                    placeholder="Your revolutionary name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                    placeholder="your.email@domain.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                    placeholder="+234 XXX XXX XXXX"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                    placeholder="Your organization"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                  Service of Interest
                </label>
                <div className="relative">
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-gray-900 text-white">Select a revolutionary service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service} className="bg-gray-900 text-white">{service}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Your Revolutionary Vision *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400 resize-none"
                  placeholder="Tell us about your project, goals, and how we can help revolutionize your world..."
                />
                <div className="text-xs text-gray-400 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white py-4 px-8 rounded-lg hover:from-[#5A02D1] hover:to-[#0066CC] transition-all duration-300 font-bold text-lg transform hover:scale-105 disabled:opacity-50 disabled:transform-none whitespace-nowrap"
              >
                {isSubmitting ? 'INITIATING REVOLUTION...' : 'START REVOLUTION'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Revolutionary CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] opacity-20" />
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
            THE REVOLUTION
            <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
              STARTS NOW
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12">
            Don&apos;t wait for the future. <span className="text-[#6D03F4] font-bold">Create it</span> with TheSpire.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="https://wa.me/2349167710173"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-12 py-4 rounded-lg font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition-colors transform hover:scale-105 duration-300 whitespace-nowrap inline-block"
            >
              INSTANT REVOLUTION
            </a>
            <a
              href="tel:+2349167710173"
              className="border-2 border-white text-white px-12 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#6D03F4] transition-colors transform hover:scale-105 duration-300 whitespace-nowrap inline-block"
            >
              CALL REVOLUTION
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/2349167710173"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 group"
        >
          <i className="ri-whatsapp-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </a>
      </div>

      <style jsx>{`
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
      `}</style>
    </div>
  );
}
